/**
 * Effects: tracers, muzzle flash, splash columns, impact sparks, smoke and fire.
 *
 * Everything here is pooled and pre-warmed (plan C7). Particle systems are expensive to
 * create and cheap to re-aim, so each effect exists once and is repositioned and burst on
 * demand rather than being constructed per event.
 *
 * Distance is the organising principle. A shell landing 5 km out must still visibly wound
 * the water, so splash scale is driven by range rather than being a fixed world size that
 * shrinks to nothing under perspective.
 */

import type { Scene } from '@babylonjs/core/scene';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4, Color3 } from '@babylonjs/core/Maths/math.color';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreatePlaneVertexData } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { PBRMetallicRoughnessMaterial } from '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';
import type { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';

import type { Projectile, Vessel, EntityId, VesselKind } from '../../sim/types';
import { VESSEL_SPECS } from '../../content/vessels';
import { MistPlugin } from '../mistPlugin';
import { createSmokeTexture, createSprayTexture, createSoftSprite } from './particleTextures';

/**
 * Hard ceiling on how long any water-impact particle may live, seconds.
 *
 * Distant hits are scaled up — a splash 5 km out has to be physically larger to occupy
 * the same pixels — and a big, slow, buoyant mist cloud reads as lingering far longer
 * than a near one with the same lifetime. Rather than scale life down with distance and
 * hope, every impact particle is bounded outright: no hit outlasts this.
 */
const IMPACT_MAX_LIFE = 2.2;

/**
 * Opacity of the mist that hangs after a splash column falls.
 *
 * Low on purpose. This is suspended droplets in air, and it should tint what is behind it
 * rather than replace it — at full opacity a handful of these overlap into one solid white
 * disc, which is the single thing that made impacts read as bubbles rather than water.
 */
const SPRAY_ALPHA = 0.34;

/**
 * Opacity of the column thrown up by an impact.
 *
 * Airborne water is never fully opaque — it is a torn sheet with sky through the gaps —
 * and holding a little transparency here is what lets the sea read through the plume
 * instead of the plume punching a white hole in it.
 */
const SPLASH_ALPHA = 0.7;

/**
 * How long a thrown column may live, seconds.
 *
 * Tighter than the general impact ceiling. A 30 mm column is a fast event: up, apart, and
 * gone. Letting it run past this made the water look like it was boiling under sustained
 * fire, because each new round arrived while the last three columns were still standing.
 */
const SPLASH_MAX_LIFE = 0.65;

/**
 * How long a wake particle survives, seconds.
 *
 * This is the wake's LENGTH control, not its fade: the trail is speed times lifetime, so
 * at the torpedo's 44 m/s running speed this leaves roughly 560 m of streak behind it.
 * Changing the weapon's speed changes the wake's length for free, which is correct — a
 * faster fish leaves a longer mark.
 *
 * Long and thin rather than short and fat, on Adam's direction after seeing it run. A
 * torpedo's mark on the surface is a line drawn across the sea, and a wake that fades
 * after a boat-length reads as spray rather than as something travelling.
 */
const WAKE_LIFETIME = 12.8;

/**
 * When the two beats the player HEARS happen, relative to the detonation event.
 *
 * Exported because the audio has to land on the same frames as the water: the lift
 * recording plays as the sea heaves, the column recording as it breaks. Duplicating
 * these numbers in the sync layer would let the two drift apart the first time either
 * is retuned, and a sound half a second off its picture reads as a bug in both.
 */
export function underwaterBurstBeats(depth: number): { lift: number; column: number } {
  const deep = Math.min(1, depth / 45);
  return { lift: 0.3, column: 0.95 + deep * 0.5 };
}

/** One scheduled beat of the depth-charge sequence. `at` counts down in seconds. */
interface BurstBeat {
  at: number;
  kind: 'flash' | 'ring' | 'lift' | 'column' | 'mist' | 'falling' | 'churn' | 'slam';
  x: number;
  z: number;
  depth: number;
  power: number;
  spread: number;
}

interface SmokeColumn {
  system: ParticleSystem;
  vesselId: EntityId;
  /** Hull kind the puff sizes are currently bound to; systems are pooled across kinds. */
  scaledFor: VesselKind | null;
}

interface RicochetStreak {
  mesh: InstancedMesh;
  velocity: Vector3;
  age: number;
  life: number;
  width: number;
  length: number;
}

export class EffectsSystem {
  private readonly scene: Scene;
  private readonly sprite: Texture;
  /** Torn droplet clump, for anything that is thrown water rather than smoke or mist. */
  private readonly spraySprite: Texture;
  /** Lumpy, edge-eroded mass, for anything that burns. */
  private readonly smokeSprite: Texture;

  private readonly splash: ParticleSystem;
  private readonly spray: ParticleSystem;
  /**
   * Flat pressure rings need their own emitter.
   *
   * `manualEmitCount` is consumed on Babylon's next particle animation, not when it is
   * assigned. The old shared-spray path restored the ordinary upward cone immediately,
   * before any ring particle existed, so tube blasts and pressure waves both emitted as
   * ordinary splash plumes.
   */
  private readonly ringSpray: ParticleSystem;
  private readonly sparks: ParticleSystem;
  private readonly blood: ParticleSystem;
  private readonly fireball: ParticleSystem;
  private readonly muzzleSmoke: ParticleSystem;
  /**
   * Torpedo surface wakes, one system per fish in the water, keyed by projectile id.
   *
   * One shared system was the two-torpedo bug Adam caught: the per-projectile loop left
   * the single emitter wherever the LAST fish was each frame, so the first fish ran
   * wakeless the moment a second launched. The pool is tiny — two tubes plus whatever
   * the minisub has swimming — and released systems are reused, smoke-column style.
   */
  private readonly wakes = new Map<number, ParticleSystem>();
  private readonly wakePool: ParticleSystem[] = [];

  private readonly muzzleLight: PointLight;
  private muzzleLightTimer = 0;

  /** The 30 mm's muzzle flash sprites — the light alone never read in daylight. */
  private readonly muzzleFlashPs: ParticleSystem;
  /**
   * Enemy gun and launcher flashes.
   *
   * Separate from the player's four-frame flicker: an enemy flash has to cross hundreds
   * of metres and identify which hull fired, so it is larger and hangs for a fraction
   * longer. One pooled system handles every hostile surface weapon.
   */
  private readonly hostileMuzzleFlashPs: ParticleSystem;

  /**
   * The depth charge's own water and mist.
   *
   * Separate systems rather than the shared splash and spray, because the depth charge
   * is the one impact in the game that is SLOW. Those two are capped at 0.65 s and
   * 2.2 s — right for a 30 mm round punching a hole in the sea, and physically unable
   * to hold a column that takes three seconds to climb. Sharing them was why the burst
   * read as a big gun splash rather than as several hundred kilos of explosive lifting
   * the ocean.
   */
  private readonly burstWater: ParticleSystem;
  private readonly burstMist: ParticleSystem;

  /** The 50 ms white-hot pop when a torpedo finds a hull. Light + additive sprites. */
  private readonly hitFlashPs: ParticleSystem;
  private readonly hitFlashLight: PointLight;
  private hitFlashTimer = 0;
  /** Wave height sampler captured from update(), so event-driven effects can sit ON the sea. */
  private surfaceFn: (x: number, z: number) => number = () => 0;
  /** Throttles wake emission so a long run does not exhaust the spray pool. */
  private wakeTimer = 0;
  /** Pending beats of any in-flight underwater burst sequence. */
  private readonly burstQueue: BurstBeat[] = [];

  /** Tracer billboards, instanced from one stretched plane. */
  private readonly tracerProto: Mesh;
  private readonly tracers: InstancedMesh[] = [];
  /** Short-lived armour deflections reuse the tracer plane and material. */
  private readonly ricochetStreaks: RicochetStreak[] = [];
  private tracerMaterial: PBRMetallicRoughnessMaterial;

  /**
   * Visible projector charges, instanced from one canister.
   *
   * The simulation has always carried the full ballistic arc and explicitly described
   * the charge tumbling through it, but the renderer drew only the bubbles after water
   * entry. Four instances cover the projector magazine without adding one mesh per shot.
   */
  private readonly depthChargeProto: Mesh;
  private readonly depthCharges: InstancedMesh[] = [];
  private readonly depthChargeMaterial: PBRMetallicRoughnessMaterial;

  private readonly smokeColumns: SmokeColumn[] = [];
  private readonly smokePool: ParticleSystem[] = [];

  /** Camera position, refreshed each frame, for distance-scaled effects. */
  private camPos = new Vector3(0, 105, 3);

  constructor(scene: Scene) {
    this.scene = scene;
    this.sprite = createSoftSprite(scene);
    this.spraySprite = createSprayTexture(scene);
    this.smokeSprite = createSmokeTexture(scene);

    this.splash = this.makeSplash();
    this.spray = this.makeSpray();
    this.ringSpray = this.makeRingSpray();
    this.sparks = this.makeSparks();
    this.blood = this.makeBlood();
    this.fireball = this.makeFireball();
    this.muzzleSmoke = this.makeMuzzleSmoke();
    this.muzzleFlashPs = this.makeMuzzleFlashPs();
    this.hostileMuzzleFlashPs = this.makeHostileMuzzleFlashPs();
    this.burstWater = this.makeBurstWater();
    this.burstMist = this.makeBurstMist();

    this.muzzleLight = new PointLight('muzzleFlash', new Vector3(0, 100, 0), scene);
    this.muzzleLight.diffuse = new Color3(1, 0.82, 0.5);
    this.muzzleLight.intensity = 0;
    this.muzzleLight.range = 220;

    this.hitFlashPs = this.makeHitFlash();
    this.hitFlashLight = new PointLight('torpedoHitFlash', new Vector3(0, 2, 0), scene);
    this.hitFlashLight.diffuse = new Color3(1, 0.88, 0.62);
    this.hitFlashLight.intensity = 0;
    this.hitFlashLight.range = 320;

    // Tracer: a small plane stretched along the round's velocity. Emissive so it reads
    // against black water at night, which is the signature image of the whole game.
    this.tracerMaterial = new PBRMetallicRoughnessMaterial('tracer', scene);
    this.tracerMaterial.baseColor = new Color3(0, 0, 0);
    this.tracerMaterial.emissiveColor = new Color3(3.2, 1.5, 0.42);
    this.tracerMaterial.metallic = 0;
    this.tracerMaterial.roughness = 1;
    this.tracerMaterial.transparencyMode = PBRMetallicRoughnessMaterial.PBRMATERIAL_ALPHABLEND;
    this.tracerMaterial.alpha = 0.92;
    this.tracerMaterial.backFaceCulling = false;
    this.tracerMaterial.disableLighting = true;
    new MistPlugin(this.tracerMaterial);

    this.tracerProto = new Mesh('tracerProto', scene);
    CreatePlaneVertexData({ width: 1, height: 1 }).applyToMesh(this.tracerProto);
    this.tracerProto.material = this.tracerMaterial;
    this.tracerProto.setEnabled(false);
    this.tracerProto.isPickable = false;
    this.tracerProto.alwaysSelectAsActiveMesh = true;

    // A blunt, heavy naval canister. Its long axis is local Y, which gives the airborne
    // rotation an unmistakable end-over-end silhouette rather than a spinning dot.
    this.depthChargeMaterial = new PBRMetallicRoughnessMaterial('depthChargeBody', scene);
    this.depthChargeMaterial.baseColor = new Color3(0.21, 0.23, 0.12);
    this.depthChargeMaterial.emissiveColor = new Color3(0.025, 0.03, 0.008);
    this.depthChargeMaterial.metallic = 0.34;
    this.depthChargeMaterial.roughness = 0.48;
    new MistPlugin(this.depthChargeMaterial);

    this.depthChargeProto = new Mesh('depthChargeProto', scene);
    CreateCylinderVertexData({
      height: 1.35,
      diameter: 0.78,
      tessellation: 12,
    }).applyToMesh(this.depthChargeProto);
    this.depthChargeProto.material = this.depthChargeMaterial;
    this.depthChargeProto.setEnabled(false);
    this.depthChargeProto.isPickable = false;
    this.depthChargeProto.alwaysSelectAsActiveMesh = true;
  }

  setCamera(pos: Vector3): void {
    this.camPos.copyFrom(pos);
  }

  /**
   * Foam and spray take their colour from the same environment the sea does.
   *
   * Each system keeps its own opacity. This used to push alpha 1 into both, which
   * silently overrode the hanging mist's intended translucency and turned it into a
   * solid white disc sitting on the water — the effect looked like a bubble because it
   * was being drawn as one.
   */
  setEnvironment(foam: { r: number; g: number; b: number }): void {
    this.splash.color1 = new Color4(foam.r, foam.g, foam.b, SPLASH_ALPHA);
    this.spray.color1 = new Color4(foam.r, foam.g, foam.b, SPRAY_ALPHA);
    this.ringSpray.color1 = new Color4(foam.r, foam.g, foam.b, SPRAY_ALPHA);
  }

  // --- Effect triggers -------------------------------------------------------------------

  /**
   * A round hitting water. Column height scales with the calibre and impact speed; the
   * whole effect scales with distance so it stays legible at range instead of vanishing.
   */
  waterImpact(
    x: number, y: number, z: number, speed: number, big: boolean, ricochet = false,
  ): void {
    const dist = Vector3.Distance(this.camPos, new Vector3(x, y, z));
    // A splash 4 km away must be physically larger to occupy the same pixels. This is a
    // deliberate cheat and the only reason distant fire reads at all.
    // A skip barely breaks the surface, so it gets a fraction of the column.
    const scale = (big ? 2.4 : 1) * (ricochet ? 0.45 : 1) * (1 + Math.min(dist / 1400, 5.5));
    const energy = Math.min(1.6, 0.5 + speed / 900);

    this.splash.emitter = new Vector3(x, y, z);
    this.splash.minSize = 0.8 * scale;
    this.splash.maxSize = 2.1 * scale;
    // About 30% less height. Ballistic apex goes with the square of launch speed, so the
    // speeds carry the square root of the height cut rather than the cut itself.
    this.splash.minEmitPower = 10.0 * energy * Math.sqrt(scale);
    this.splash.maxEmitPower = 21.8 * energy * Math.sqrt(scale);
    this.splash.manualEmitCount = ricochet ? 8 : (big ? 46 : 22);

    this.spray.emitter = new Vector3(x, y, z);
    // Half the former size: these are the round soft-sprite puffs, and at the old scale a
    // few of them overlapped into one bubble wider than the column that threw them.
    this.spray.minSize = 1.2 * scale;
    this.spray.maxSize = 3.5 * scale;
    // Scale the throw by the fourth root rather than the square root: a distant splash
    // should read as larger, not as one that also drifts for longer.
    const drift = Math.pow(scale, 0.25);
    this.spray.minEmitPower = 3 * drift;
    this.spray.maxEmitPower = 9 * drift;
    this.spray.manualEmitCount = ricochet ? 5 : (big ? 30 : 14);
  }

  /**
   * A round striking a hull.
   *
   * Ordinary hits fragment into a spark fan. An armoured tracer hit adds a coherent
   * deflection streak: this is presentation only, while the simulation's armour matrix
   * remains the sole damage authority.
   */
  hullImpact(
    x: number,
    y: number,
    z: number,
    ricochet = false,
    deflectSign: -1 | 1 = 1,
  ): void {
    const dist = Vector3.Distance(this.camPos, new Vector3(x, y, z));
    const scale = 1 + Math.min(dist / 1600, 4);
    this.sparks.emitter = new Vector3(x, y, z);
    this.sparks.minSize = 0.25 * scale;
    this.sparks.maxSize = 0.9 * scale;
    this.sparks.manualEmitCount = ricochet ? 16 : 26;

    if (!ricochet) return;

    /*
     * The player sees a glancing path, not a physics diagram. Turn the incoming sightline
     * roughly ninety degrees around vertical and lift it, so the hot tracer visibly
     * leaves the hull instead of hiding inside the ordinary radial spark fan.
     */
    const incoming = new Vector3(
      x - this.camPos.x,
      y - this.camPos.y,
      z - this.camPos.z,
    ).normalize();
    const outgoing = new Vector3(
      -incoming.z * deflectSign,
      0.28 + Math.abs(incoming.y) * 0.35,
      incoming.x * deflectSign,
    ).normalize();
    this.startRicochetStreak(x, y, z, outgoing, scale);
  }

  /**
   * A round striking flesh rather than plate (Adam, 2026-07-30: hitting the Kraken
   * "shouldn't look like hitting the hull of a ship — if anything it should emit a
   * *small* amount of red-purple blood").
   *
   * Deliberately understated next to the metal spark fan: a handful of dark heavy
   * droplets that fall rather than fly, standard-blended because blood absorbs light —
   * an additive burst would read as fire on a creature that does not burn.
   */
  fleshImpact(x: number, y: number, z: number): void {
    const dist = Vector3.Distance(this.camPos, new Vector3(x, y, z));
    const scale = 1 + Math.min(dist / 1600, 4);
    this.blood.emitter = new Vector3(x, y, z);
    this.blood.minSize = 0.5 * scale;
    this.blood.maxSize = 1.5 * scale;
    this.blood.manualEmitCount = 9;
  }

  /** A large detonation — magazine, fuel, torpedo or depth charge. */
  detonation(x: number, y: number, z: number, radius: number): void {
    const dist = Vector3.Distance(this.camPos, new Vector3(x, y, z));
    const scale = (radius / 30) * (1 + Math.min(dist / 2000, 4));
    this.fireball.emitter = new Vector3(x, y, z);
    this.fireball.minSize = 5 * scale;
    this.fireball.maxSize = 16 * scale;
    this.fireball.manualEmitCount = 40;

    this.muzzleLight.position.set(x, y + 10, z);
    this.muzzleLight.diffuse.set(1, 0.55, 0.2);
    this.muzzleLight.intensity = 900 * scale;
    this.muzzleLightTimer = 0.3;
  }

  /**
   * An underwater burst: the eight-beat depth-charge sequence (plan D3).
   *
   * The beats before the eruption are the whole effect. Anyone can throw a white column
   * up out of the sea; what says "several hundred kilos of explosive went off thirty
   * metres DOWN" is the quarter-second where the water glows, swells and distorts while
   * the surface is still intact. Beats run on a scheduled queue rather than one burst,
   * because the delays between them are the content — and every timing here is exposed
   * so the feel gate can tune them without touching code.
   *
   * Depth scales the sequence in opposite directions on purpose: a deeper burst gives a
   * dimmer, later, broader eruption, so the fuse selector is legible in the FX itself.
   */
  underwaterBurst(x: number, z: number, depth: number): void {
    const dist = Vector3.Distance(this.camPos, new Vector3(x, 0, z));
    const rangeScale = 1 + Math.min(dist / 2000, 4);
    // Deep bursts vent less violently but disturb a wider patch.
    const deep = Math.min(1, depth / 45);
    const power = (1 - deep * 0.45) * rangeScale;
    const spread = (1 + deep * 0.7) * rangeScale;

    /*
     * The sequence, retimed on Adam's direction: the sea is lifted from below before
     * anything breaks, then the column goes up ten times the size of a 30 mm splash and
     * five times slower, throwing mist that outlives it.
     *
     * The lift is the beat that was missing. Water does not open a hole and spray — a
     * charge going off thirty metres down pushes a dome of unbroken sea upward first,
     * and only when that dome fails does the white water come through it. Two lift
     * beats, wide and slow, buy that half-second of "something enormous is coming".
     */
    // ① Compressed flash at the charge, dimmed by the water above it.
    this.burstQueue.push({ at: 0, kind: 'flash', x, z, depth, power, spread });
    // ②③ Pressure ring and distortion, still below an unbroken surface.
    this.burstQueue.push({ at: 0.1, kind: 'ring', x, z, depth, power, spread });
    this.burstQueue.push({ at: 0.2, kind: 'ring', x, z, depth, power: power * 0.7, spread: spread * 1.5 });
    // ④⑤ The sea heaves up in a dome, widening as it rises.
    const beats = underwaterBurstBeats(depth);
    this.burstQueue.push({ at: beats.lift, kind: 'lift', x, z, depth, power, spread });
    this.burstQueue.push({
      at: beats.lift + 0.32, kind: 'lift', x, z, depth, power: power * 1.35, spread: spread * 1.3,
    });
    // ⑥⑦ The dome fails and the column comes through it, then broadens.
    this.burstQueue.push({ at: beats.column, kind: 'column', x, z, depth, power, spread });
    this.burstQueue.push({
      at: beats.column + 0.4, kind: 'column', x, z, depth, power: power * 0.72, spread: spread * 1.45,
    });
    // ⑧⑨ Mist, with the column and then above it — the part that carries the scale.
    this.burstQueue.push({ at: beats.column + 0.3, kind: 'mist', x, z, depth, power, spread });
    this.burstQueue.push({
      at: 2.3 + deep * 0.5, kind: 'mist', x, z, depth, power: power * 0.8, spread: spread * 1.5,
    });
    // ⑩ Foam ring pushed out across the surface.
    this.burstQueue.push({ at: 1.6 + deep * 0.5, kind: 'ring', x, z, depth, power: power * 0.5, spread: spread * 2.4 });
    // ⑪⑫ The sheet coming back down, and the churned patch left behind.
    this.burstQueue.push({ at: 3.4 + deep * 0.5, kind: 'falling', x, z, depth, power, spread });
    this.burstQueue.push({ at: 4.8, kind: 'churn', x, z, depth, power, spread });
  }

  /** Fire any burst beats whose time has come. */
  private stepBurstQueue(dt: number): void {
    if (this.burstQueue.length === 0) return;
    for (const beat of this.burstQueue) beat.at -= dt;

    let write = 0;
    for (const beat of this.burstQueue) {
      if (beat.at > 0) {
        this.burstQueue[write++] = beat;
        continue;
      }
      this.playBurstBeat(beat);
    }
    this.burstQueue.length = write;
  }

  private playBurstBeat(b: BurstBeat): void {
    const { x, z, power, spread } = b;
    switch (b.kind) {
      case 'flash': {
        // Light from below: positioned at the charge, so it glows up through the water.
        this.muzzleLight.position.set(x, -b.depth * 0.5, z);
        this.muzzleLight.diffuse.set(0.55, 0.85, 1.0);
        this.muzzleLight.intensity = 1400 * power;
        this.muzzleLightTimer = 0.22;
        break;
      }
      case 'ring': {
        // A flat, fast-spreading disc of disturbed water — no vertical throw at all.
        this.ringSpray.emitter = new Vector3(x, 0.3, z);
        this.ringSpray.minSize = 3 * spread;
        this.ringSpray.maxSize = 9 * spread;
        this.ringSpray.minEmitPower = 14 * power;
        this.ringSpray.maxEmitPower = 26 * power;
        this.ringSpray.manualEmitCount = 22;
        break;
      }
      case 'lift': {
        /*
         * The sea coming up from below: a wide, flat patch of water rising slowly and
         * barely breaking up. Emitted across a disc rather than from a point, which is
         * what makes it read as a surface heaving rather than as a plume — the emit box
         * is restored afterwards so the column that follows stays a column.
         */
        const radius = 6.5 * spread;
        this.burstWater.emitter = new Vector3(x, 0.2, z);
        this.burstWater.minEmitBox = new Vector3(-radius, 0, -radius);
        this.burstWater.maxEmitBox = new Vector3(radius, 0.4, radius);
        this.burstWater.minSize = 8 * spread;
        this.burstWater.maxSize = 18 * spread;
        this.burstWater.minEmitPower = 3 * power;
        this.burstWater.maxEmitPower = 7 * power;
        this.burstWater.manualEmitCount = 42;
        this.burstWater.minEmitBox = new Vector3(0, 0, 0);
        this.burstWater.maxEmitBox = new Vector3(0, 0, 0);
        break;
      }
      case 'column': {
        // The eruption proper: ten times a 30 mm splash, climbing for two and a half
        // seconds under the reduced gravity this system carries.
        this.burstWater.emitter = new Vector3(x, 0.4, z);
        this.burstWater.minSize = 7 * spread;
        this.burstWater.maxSize = 17 * spread;
        this.burstWater.minEmitPower = 21 * power;
        this.burstWater.maxEmitPower = 37 * power;
        this.burstWater.manualEmitCount = 130;
        this.muzzleLight.position.set(x, 12, z);
        this.muzzleLight.diffuse.set(0.9, 0.95, 1.0);
        this.muzzleLight.intensity = 500 * power;
        this.muzzleLightTimer = 0.18;
        break;
      }
      case 'slam': {
        /*
         * The Kraken's arm hitting the sea, on the FAST splash system.
         *
         * Deliberately not the depth charge's water: that was retimed to climb for two
         * and a half seconds, which is right for an explosion lifting the ocean and
         * quite wrong for a limb coming down — the slam has to land on the same frame
         * the player sees it strike. This is the old bulge-and-column pair, kept
         * together so the boss keeps the punch it was tuned with.
         */
        this.splash.emitter = new Vector3(x, 0.3, z);
        this.splash.minSize = 4 * spread;
        this.splash.maxSize = 11 * spread;
        this.splash.minEmitPower = 20 * power;
        this.splash.maxEmitPower = 44 * power;
        this.splash.manualEmitCount = 80;
        break;
      }
      case 'mist': {
        // Lots of it, and going up: this is what tells the player how much sea moved.
        const radius = 4 * spread;
        this.burstMist.emitter = new Vector3(x, 2.5, z);
        this.burstMist.minEmitBox = new Vector3(-radius, 0, -radius);
        this.burstMist.maxEmitBox = new Vector3(radius, 3, radius);
        this.burstMist.minSize = 11 * spread;
        this.burstMist.maxSize = 28 * spread;
        this.burstMist.minEmitPower = 5 * power;
        this.burstMist.maxEmitPower = 13 * power;
        this.burstMist.manualEmitCount = 78;
        break;
      }
      case 'falling': {
        // Sheets coming back down, which is what gives the column its real scale.
        const radius = 6 * spread;
        this.burstMist.emitter = new Vector3(x, 18 * power, z);
        this.burstMist.minEmitBox = new Vector3(-radius, 0, -radius);
        this.burstMist.maxEmitBox = new Vector3(radius, 2, radius);
        this.burstMist.minSize = 6 * spread;
        this.burstMist.maxSize = 15 * spread;
        this.burstMist.minEmitPower = 1;
        this.burstMist.maxEmitPower = 5;
        this.burstMist.manualEmitCount = 45;
        break;
      }
      case 'churn': {
        const radius = 7 * spread;
        this.burstMist.emitter = new Vector3(x, 0.4, z);
        this.burstMist.minEmitBox = new Vector3(-radius, 0, -radius);
        this.burstMist.maxEmitBox = new Vector3(radius, 1, radius);
        this.burstMist.minSize = 8 * spread;
        this.burstMist.maxSize = 20 * spread;
        this.burstMist.minEmitPower = 0.5;
        this.burstMist.maxEmitPower = 2.5;
        this.burstMist.manualEmitCount = 34;
        break;
      }
    }
  }

  /**
   * The tube blast: a torpedo being pushed out on compressed air (plan D2).
   *
   * A flat ring rather than a cone, because the charge vents sideways along the hull —
   * and because a vertical burst here would be indistinguishable from the water entry
   * that follows a beat later. Two events, two silhouettes.
   */
  tubeBlast(x: number, y: number, z: number, dirX: number, dirZ: number): void {
    const dist = Vector3.Distance(this.camPos, new Vector3(x, y, z));
    const scale = 1 + Math.min(dist / 900, 3);

    this.ringSpray.emitter = new Vector3(x + dirX * 4, y, z + dirZ * 4);
    this.ringSpray.minSize = 1.4 * scale;
    this.ringSpray.maxSize = 3.6 * scale;
    this.ringSpray.minEmitPower = 9;
    this.ringSpray.maxEmitPower = 20;
    this.ringSpray.manualEmitCount = 18;

    // A little smoke off the venting charge, so the launch has a body at close range.
    this.muzzleSmoke.emitter = new Vector3(x + dirX * 2, y + 1, z + dirZ * 2);
    this.muzzleSmoke.minSize = 0.9 * scale;
    this.muzzleSmoke.maxSize = 3.0 * scale;
    this.muzzleSmoke.manualEmitCount = 10;
  }

  /**
   * The Kraken bringing an arm down on the sea (plan E8 P2).
   *
   * Every phase of the boss was legible only through the HUD and the phase machine —
   * a wave slam that costs the player six integrity has to be something they SAW happen,
   * or the damage reads as arbitrary. Reuses the burst queue so the beats land in order:
   * the impact, then the water going up, then the collapse.
   */
  krakenSlam(x: number, z: number): void {
    this.burstQueue.push({ at: 0, kind: 'slam', x, z, depth: 0, power: 2.2, spread: 2.6 });
    this.burstQueue.push({ at: 0.06, kind: 'slam', x, z, depth: 0, power: 1.5, spread: 2.2 });
    this.burstQueue.push({ at: 0.30, kind: 'ring', x, z, depth: 0, power: 1.1, spread: 3.4 });
    this.burstQueue.push({ at: 0.9, kind: 'falling', x, z, depth: 0, power: 1.4, spread: 2.4 });
    this.burstQueue.push({ at: 1.8, kind: 'churn', x, z, depth: 0, power: 1.0, spread: 3.0 });
  }

  /**
   * The instant a torpedo finds a hull: 50 ms of overexposure at the point of impact
   * (Adam's call), one hard frame of white before the slower fire and water take over.
   * The light sells it near — hull side and sea lit for a blink — and the additive
   * sprites, distance-scaled like the wake, carry the pop out to ranges where a point
   * light is invisible.
   */
  torpedoHitFlash(x: number, y: number, z: number): void {
    const surface = this.surfaceFn(x, z);
    const dist = Vector3.Distance(this.camPos, new Vector3(x, y, z));
    const scale = 1 + Math.min(dist / 1600, 4);

    this.hitFlashPs.emitter = new Vector3(x, Math.max(y, surface) + 1.6, z);
    this.hitFlashPs.minSize = 5 * scale;
    this.hitFlashPs.maxSize = 9 * scale;
    this.hitFlashPs.manualEmitCount = 3;

    this.hitFlashLight.position.set(x, surface + 3, z);
    this.hitFlashLight.intensity = 850;
    this.hitFlashTimer = 0.05;
  }

  /** Muzzle flash at the gun. */
  muzzleFlash(x: number, y: number, z: number, dirX: number, dirY: number, dirZ: number): void {
    /*
     * Bias the plume to the gunner's left, on Adam's direction: the muzzle sits west of
     * the stance and the sight picture is straight ahead, so smoke pushed further left
     * clears the view instead of drifting across it. `left` is the aim bearing turned
     * ninety degrees — for a barrel pointing north that is due west.
     */
    const leftX = -dirZ;
    const leftZ = dirX;
    const DRIFT = 5;
    this.muzzleSmoke.emitter = new Vector3(
      x + dirX * 3 + leftX * 0.6, y + dirY * 3, z + dirZ * 3 + leftZ * 0.6,
    );
    this.muzzleSmoke.direction1 = new Vector3(
      dirX * 8 - 1 + leftX * DRIFT, dirY * 8, dirZ * 8 - 1 + leftZ * DRIFT,
    );
    this.muzzleSmoke.direction2 = new Vector3(
      dirX * 14 + 1 + leftX * DRIFT, dirY * 14 + 1, dirZ * 14 + 1 + leftZ * DRIFT,
    );
    // Two puffs, not five: see makeMuzzleSmoke — the gun is three metres away now.
    this.muzzleSmoke.manualEmitCount = 2;

    /*
     * The flash itself, which the gun never actually had: only a point light, invisible
     * in daylight against bright water. A pair of additive sprites at the brake give it
     * a body. Deliberately small and short — Adam asked for subtle, and at 300 rpm
     * anything bigger strobes the whole sight picture.
     */
    this.muzzleFlashPs.emitter = new Vector3(x + dirX * 0.5, y + dirY * 0.5, z + dirZ * 0.5);
    this.muzzleFlashPs.manualEmitCount = 3;

    this.muzzleLight.position.set(x + dirX * 2, y + dirY * 2, z + dirZ * 2);
    this.muzzleLight.diffuse.set(1, 0.82, 0.5);
    this.muzzleLight.intensity = 320;
    this.muzzleLight.range = 220;
    this.muzzleLightTimer = 0.06;
  }

  /**
   * A readable origin for hostile fire.
   *
   * The simulation event is emitted from the hull centre because that is where its
   * projectile starts. Presentation lifts and advances the flash to the visible weapon:
   * small craft carry guns near the bow, while the FAC and corvette launch from amidship
   * missile cells. The mini-sub stays dark; a submerged torpedo should not make an
   * airborne fireball.
   */
  hostileMuzzleFlash(
    x: number,
    y: number,
    z: number,
    dirX: number,
    dirY: number,
    dirZ: number,
    kind: Vessel['kind'],
    sourceHeading: number,
  ): void {
    if (kind === 'minisub' || kind === 'tentacle' || kind === 'kraken') return;

    const hardpoint = kind === 'corvette'
      // The VLS lid is almost eight metres above the waterline; the event already
      // starts 2.5 m up, and the sprite's lower edge needs to meet the deck rather than
      // being depth-hidden inside it.
      ? { lift: 9.0, forward: 0, calibre: 1.75, particles: 10 }
      : kind === 'fac'
        ? { lift: 1.8, forward: 0, calibre: 1.35, particles: 8 }
        : kind === 'gunboat'
          ? { lift: 1.6, forward: 8.5, calibre: 1.0, particles: 6 }
          : { lift: 0.8, forward: 1.5, calibre: 0.72, particles: 4 };
    const forwardX = Math.sin(sourceHeading);
    const forwardZ = Math.cos(sourceHeading);
    const px = x + forwardX * hardpoint.forward;
    const py = y + hardpoint.lift;
    const pz = z + forwardZ * hardpoint.forward;
    const distance = Vector3.Distance(this.camPos, new Vector3(px, py, pz));
    const distanceScale = 1 + Math.min(distance / 1200, 1.2);
    const scale = hardpoint.calibre * distanceScale;

    this.hostileMuzzleFlashPs.emitter = new Vector3(px, py, pz);
    this.hostileMuzzleFlashPs.direction1 = new Vector3(
      dirX * 4 - 0.8,
      dirY * 4 - 0.3,
      dirZ * 4 - 0.8,
    );
    this.hostileMuzzleFlashPs.direction2 = new Vector3(
      dirX * 7 + 0.8,
      dirY * 7 + 1.0,
      dirZ * 7 + 0.8,
    );
    this.hostileMuzzleFlashPs.minSize = 0.8 * scale;
    this.hostileMuzzleFlashPs.maxSize = 1.7 * scale;
    // FAC salvos emit four events in one simulation tick. Accumulate them before
    // Babylon consumes the count on the next particle animation.
    const alreadyQueued = Math.max(0, this.hostileMuzzleFlashPs.manualEmitCount);
    this.hostileMuzzleFlashPs.manualEmitCount = alreadyQueued + hardpoint.particles;

    this.muzzleLight.position.set(px + dirX, py + dirY, pz + dirZ);
    this.muzzleLight.diffuse.set(1, 0.62, 0.25);
    this.muzzleLight.intensity = 440 * hardpoint.calibre;
    this.muzzleLight.range = 260 * hardpoint.calibre;
    this.muzzleLightTimer = 0.13;
  }

  // --- Per-frame ------------------------------------------------------------------------

  /**
   * @param surfaceAt Wave height at a world XZ. Effects that sit ON the sea need it: a
   *   wake emitted at a fixed y=0.5 spawns *inside* the water whenever a wave is up,
   *   where the depth test hides it — which in sea state 4.8 is most of the time, and is
   *   why the torpedo appeared to leave no wake at all.
   */
  update(
    dt: number,
    projectiles: readonly Projectile[],
    vessels: readonly Vessel[],
    surfaceAt: (x: number, z: number) => number = () => 0,
  ): void {
    if (this.muzzleLightTimer > 0) {
      this.muzzleLightTimer -= dt;
      if (this.muzzleLightTimer <= 0) this.muzzleLight.intensity = 0;
      else this.muzzleLight.intensity *= 0.78;
    }

    // The hit flash holds full intensity and cuts — a decay would read as a small fire,
    // and 50 ms is too short to shape anyway.
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= dt;
      if (this.hitFlashTimer <= 0) this.hitFlashLight.intensity = 0;
    }

    this.surfaceFn = surfaceAt;
    this.stepBurstQueue(dt);
    this.updateTracers(projectiles);
    this.updateRicochetStreaks(dt);
    this.updateOrdnance(projectiles, dt, surfaceAt);
    this.updateSmokeColumns(vessels);
  }

  /**
   * Wake and bubble trails for ordnance that runs rather than flies.
   *
   * A torpedo's wake is the only thing the player can actually see of it once it is
   * under: the body is four metres down and the sea is opaque. The feather has to
   * strengthen as the weapon shallows and speeds up, because that rising visibility is
   * how the run reads as building power rather than as a marker sliding across the water.
   */
  private updateOrdnance(
    projectiles: readonly Projectile[],
    dt: number,
    surfaceAt: (x: number, z: number) => number,
  ): void {
    this.wakeTimer -= dt;
    const emit = this.wakeTimer <= 0;
    if (emit) this.wakeTimer = 0.045;

    const liveWakes = new Set<number>();
    let visibleCharges = 0;

    for (const p of projectiles) {
      if (!p.active) continue;

      if (p.kind === 'torpedo' && p.phase !== 'air') {
        liveWakes.add(p.id);
        if (!emit) continue;
        const wake = this.wakes.get(p.id) ?? this.claimWake(p.id);
        if (!wake) continue;
        const speed = Math.hypot(p.vel.x, p.vel.z);
        // A shallow, fast weapon throws a hard white feather; a slow one barely marks the
        // surface. Rising visibility through the run is what reads as building power.
        const strength = Math.min(1, Math.max(0.12, (speed - 4) / 26));
        const dist = Vector3.Distance(this.camPos, new Vector3(p.pos.x, 0, p.pos.z));
        // Same distance cheat the splashes use: a wake 3 km out must still be a line.
        const scale = 1 + Math.min(dist / 1600, 4);

        // Ride the surface, plus a little, so the foam is always on top of the water.
        wake.emitter = new Vector3(p.pos.x, surfaceAt(p.pos.x, p.pos.z) + 0.8, p.pos.z);
        // A quarter of the old width: this is a line on the water, not a foam bank.
        wake.minSize = (0.40 + strength * 0.55) * scale;
        wake.maxSize = (0.85 + strength * 1.00) * scale;
        wake.manualEmitCount = 2;
      }

      if (p.kind === 'depthCharge') {
        let body = this.depthCharges[visibleCharges];
        if (!body) {
          body = this.depthChargeProto.createInstance(`depthCharge_${visibleCharges}`);
          body.isPickable = false;
          body.alwaysSelectAsActiveMesh = true;
          this.depthCharges.push(body);
        }
        body.setEnabled(true);
        body.position.set(p.pos.x, p.pos.y, p.pos.z);
        // Like distant splashes and torpedo wakes, the body receives a restrained
        // perspective cheat. A physically exact 78 cm canister at 600 m is roughly one
        // pixel even at 2×, which makes "visible tumbling flight" mathematically
        // impossible. It stays true-size nearby and tops out at 4× downrange.
        const bodyDistance = Vector3.Distance(this.camPos, body.position);
        const readabilityScale = 1 + Math.min(3, Math.max(0, bodyDistance - 80) / 220);
        body.scaling.setAll(readabilityScale);

        const phase = p.id * 0.731;
        if (p.phase === 'air') {
          // Different rates on all three axes prevent the repeated, mechanical coin-flip
          // motion that one-axis rotation produces. Projectile age keeps it deterministic.
          body.rotation.set(
            p.age * 5.4 + phase,
            p.age * 2.1 + phase * 0.37,
            p.age * 4.2 - phase * 0.61,
          );
        } else {
          // Once the canister hits water it rights itself and keeps only a slow residual
          // wobble. Most of the body is depth-occluded; the bubbles carry the track.
          body.rotation.set(
            Math.sin(p.age * 1.3 + phase) * 0.11,
            p.age * 0.42 + phase,
            Math.cos(p.age * 1.1 + phase) * 0.09,
          );
        }
        visibleCharges++;

        if (p.phase === 'sinking' && emit) {
          // Bubbles marking the sinking charge — the only cue to where it actually is.
          this.spray.emitter = new Vector3(p.pos.x, surfaceAt(p.pos.x, p.pos.z) + 0.5, p.pos.z);
          this.spray.minSize = 0.7;
          this.spray.maxSize = 1.8;
          this.spray.minEmitPower = 0.3;
          this.spray.maxEmitPower = 1.1;
          this.spray.manualEmitCount = 1;
        }
      }
    }

    for (let i = visibleCharges; i < this.depthCharges.length; i++) {
      (this.depthCharges[i] as InstancedMesh).setEnabled(false);
    }

    // A fish that detonated or sank gives its wake system back; the trail it already
    // laid fades out on its own lifetime.
    for (const [id, ps] of this.wakes) {
      if (liveWakes.has(id)) continue;
      ps.manualEmitCount = 0;
      this.wakes.delete(id);
      this.wakePool.push(ps);
    }
  }

  /** A wake system for a fish that just entered the water: pooled, or built to a cap. */
  private claimWake(id: number): ParticleSystem | null {
    let ps = this.wakePool.pop() ?? null;
    if (!ps) {
      // Two tubes plus a hostile fish or two. A fifth simultaneous torpedo goes
      // wakeless rather than growing the pool without bound.
      if (this.wakes.size >= 4) return null;
      ps = this.makeWake();
    }
    this.wakes.set(id, ps);
    return ps;
  }

  /**
   * Draw a stretched billboard for every tracer in the air.
   *
   * The streak length is the distance the round covers in a fixed slice of time, so a
   * fast round draws a long streak and a slowing one shortens — which is what makes the
   * stream read as 3D projectiles rather than a chain of dots.
   */
  private updateTracers(projectiles: readonly Projectile[]): void {
    let used = 0;

    for (const p of projectiles) {
      if (!p.active) continue;
      if (!p.tracer && p.kind !== 'rocket' && p.kind !== 'rpg') continue;
      if (p.phase !== 'air') continue;

      let inst = this.tracers[used];
      if (!inst) {
        inst = this.tracerProto.createInstance(`tracer_${used}`);
        inst.isPickable = false;
        inst.alwaysSelectAsActiveMesh = true;
        this.tracers.push(inst);
      }
      inst.setEnabled(true);

      const speed = Math.hypot(p.vel.x, p.vel.y, p.vel.z);
      const streak = Math.max(6, speed * 0.028);

      // Place the billboard at the round, oriented along its velocity, and turned to face
      // the camera about that axis so it is never seen edge-on.
      inst.position.set(p.pos.x, p.pos.y, p.pos.z);

      const dir = new Vector3(p.vel.x / speed, p.vel.y / speed, p.vel.z / speed);
      const toCam = this.camPos.subtract(inst.position).normalize();
      const right = Vector3.Cross(dir, toCam).normalize();
      const up = Vector3.Cross(right, dir).normalize();
      // Build a rotation from the basis (right, up, dir).
      const m = inst.getWorldMatrix();
      m.setRowFromFloats(0, right.x, right.y, right.z, 0);
      m.setRowFromFloats(1, dir.x, dir.y, dir.z, 0);
      m.setRowFromFloats(2, up.x, up.y, up.z, 0);
      inst.rotation = Vector3.Zero();
      inst.lookAt(inst.position.add(dir), 0, Math.PI / 2, 0);

      const width = p.kind === 'shell' ? 0.55 : 1.1;
      inst.scaling.set(width, streak, 1);

      used++;
    }

    for (let i = used; i < this.tracers.length; i++) {
      (this.tracers[i] as InstancedMesh).setEnabled(false);
    }
  }

  /** Claim a pooled tracer plane and send it away from the armour strike. */
  private startRicochetStreak(
    x: number,
    y: number,
    z: number,
    direction: Vector3,
    scale: number,
  ): void {
    let streak = this.ricochetStreaks.find((candidate) => candidate.age >= candidate.life);
    if (!streak) {
      if (this.ricochetStreaks.length >= 8) {
        streak = this.ricochetStreaks.reduce((oldest, candidate) =>
          candidate.age > oldest.age ? candidate : oldest);
      } else {
        const mesh = this.tracerProto.createInstance(
          `armourRicochet_${this.ricochetStreaks.length}`,
        );
        mesh.isPickable = false;
        mesh.alwaysSelectAsActiveMesh = true;
        streak = {
          mesh,
          velocity: Vector3.Zero(),
          age: 1,
          life: 0,
          width: 1,
          length: 1,
        };
        this.ricochetStreaks.push(streak);
      }
    }

    streak.age = 0;
    streak.life = 0.34;
    streak.width = 0.90 * scale;
    streak.length = 22 * scale;
    streak.velocity.copyFrom(direction).scaleInPlace(72);
    // Centre the plane just clear of the plate while letting its tail touch the strike.
    streak.mesh.position.set(
      x + direction.x * 5.5,
      y + 1.2 + direction.y * 5.5,
      z + direction.z * 5.5,
    );
    streak.mesh.visibility = 1;
    streak.mesh.setEnabled(true);
    this.orientRicochetStreak(streak);
  }

  private updateRicochetStreaks(dt: number): void {
    for (const streak of this.ricochetStreaks) {
      if (streak.age >= streak.life) {
        streak.mesh.setEnabled(false);
        continue;
      }
      streak.age += dt;
      if (streak.age >= streak.life) {
        streak.mesh.setEnabled(false);
        continue;
      }
      streak.mesh.position.addInPlace(streak.velocity.scale(dt));
      streak.velocity.y -= 7 * dt;
      streak.mesh.visibility = Math.max(0, 1 - streak.age / streak.life);
      this.orientRicochetStreak(streak);
    }
  }

  private orientRicochetStreak(streak: RicochetStreak): void {
    const speed = streak.velocity.length();
    if (speed < 1e-4) return;
    const dir = streak.velocity.scale(1 / speed);
    streak.mesh.lookAt(streak.mesh.position.add(dir), 0, Math.PI / 2, 0);
    const life = Math.max(0, 1 - streak.age / streak.life);
    streak.mesh.scaling.set(
      streak.width * (0.45 + life * 0.55),
      streak.length * (0.55 + life * 0.45),
      1,
    );
  }

  /**
   * Smoke and fire from dying ships.
   *
   * Per creative direction the smoke starts the moment a hull is killed and keeps running
   * through the settle, so a tanker is a black column on the horizon for the two and a
   * half minutes she takes to go down. The column is anchored to the hull, so it moves
   * with her as she loses way and drifts.
   */
  private updateSmokeColumns(vessels: readonly Vessel[]): void {
    const sinking = vessels.filter((v) => v.state === 'sinking');

    // Retire columns whose vessel has gone.
    for (let i = this.smokeColumns.length - 1; i >= 0; i--) {
      const col = this.smokeColumns[i] as SmokeColumn;
      const still = sinking.find((v) => v.id === col.vesselId);
      if (!still) {
        col.system.stop();
        this.smokePool.push(col.system);
        this.smokeColumns.splice(i, 1);
      }
    }

    for (const v of sinking) {
      let col = this.smokeColumns.find((c) => c.vesselId === v.id);
      if (!col) {
        const system = this.smokePool.pop() ?? this.makeSmokeColumn();
        system.start();
        col = { system, vesselId: v.id, scaledFor: null };
        this.smokeColumns.push(col);
      }

      const spec = VESSEL_SPECS[v.kind];
      if (col.scaledFor !== v.kind) {
        this.setSmokeScale(col.system, spec.length);
        col.scaledFor = v.kind;
      }
      // Emit from the superstructure, not the waterline.
      const emitY = v.pos.y + spec.height * 0.35;
      col.system.emitter = new Vector3(v.pos.x, emitY, v.pos.z);

      // Bigger ships burn bigger. The rate eases off as she goes under and the fire
      // drowns — the column thins out before it stops.
      const submerged = Math.max(0, v.stateTimer - spec.smokeDuration) / spec.sinkDuration;
      const drown = Math.max(0, 1 - submerged * 1.35);
      // Trimmed again with the second slowdown's longer lifetimes so a tanker's alive
      // count stays inside the 2400 pool; the growing puffs carry the density.
      /*
       * Rate is bounded by the pool, not by taste: a parcel now lives up to 82 s, so at
       * the old rate a tanker would ask for 4,400 live particles against a 3,000 capacity
       * and the system would quietly stop emitting mid-column. Fewer, far bigger, far
       * more opaque billows is what "denser" means here — density is coming from the
       * size and alpha passes, and asking for more parcels on top of those would only
       * starve the top of the plume.
       */
      col.system.emitRate = (4 + spec.length * 0.12) * drown;
      /*
       * Floored, not purely proportional. Scaling puff size by hull length is right for
       * a tanker and wrong for a skiff: a 9 m boat produced half-metre puffs whose
       * drift spacing was tens of metres, so they could never overlap into a plume and
       * rose as a procession of individual black dots. A smoke particle represents a
       * parcel of hot gas, and hot gas does not come smaller because the boat did.
       * Tripled across the board on Adam's "200% larger".
       */
      // Same 40% cut as the constructor's rise, which these overwrite every frame.
      col.system.minEmitPower = (2 + spec.length * 0.02) * 0.056;
      col.system.maxEmitPower = (5 + spec.length * 0.05) * 0.056;
    }
  }

  /**
   * Drop every burning column on the spot, for a change of watch.
   *
   * `stop()` is not enough and quietly became much worse: it ends emission but leaves
   * live parcels to finish their lifetime, and a parcel now lives up to 82 s. So the
   * ordinary retire path — which is right mid-shift, where a column should thin out after
   * its ship has gone under — left the last shift's smoke hanging over the new one's
   * empty sea for well over a minute. `reset()` is the one that actually clears the
   * particles.
   *
   * Paired with the wreck traces in SimSync's constructor: same boundary, same reasoning,
   * and both are things that belong to one watch.
   */
  clearSmokeColumns(): void {
    for (const col of this.smokeColumns) {
      col.system.stop();
      col.system.reset();
      this.smokePool.push(col.system);
    }
    this.smokeColumns.length = 0;
  }

  /**
   * Size the column's puffs to the hull that is burning.
   *
   * This has to be set through gradients rather than `minSize`/`maxSize`, and that is the
   * whole bug: Babylon's size gradients do not scale the emitted size, they *replace* it —
   * `_ProcessSizeGradients` assigns `particle.size` outright. So the per-hull sizes this
   * class was setting every frame (41 m to 207 m for a tanker) had never once reached the
   * screen, and every ship in the game burned with the same 0.6-to-2.0 m puffs the
   * constructor asked for. At any real engagement range that is a column one pixel wide,
   * which is why a tanker could be on fire nine hundred metres out and show nothing at
   * all — the smoke was running the entire time, invisibly.
   *
   * The two numbers that survived the confusion said what the intent was: born at the
   * emitted size, swelling as it climbs. That is what these gradients now do, in metres,
   * against the hull's own length. The second argument to each is Babylon's random upper
   * bound, which is what keeps a column from reading as one stamp repeated.
   *
   * Bound when a pooled system is handed to a new kind of hull, not per frame — rebuilding
   * a gradient list every tick would rewire the processing chain sixty times a second.
   */
  private setSmokeScale(ps: ParticleSystem, hullLength: number): void {
    // Adam, 2026-07-30, on first sight of the fixed column: "MUCH larger, at least 300%".
    // Roughly four times through, which puts a tanker's billows at 60-90 m as they leave
    // the fire and 130-170 m by the top of the climb — the scale at which a burning ship
    // is a landmark on the horizon rather than a smudge above it.
    const born = Math.max(9, hullLength * 0.30);
    ps.removeSizeGradient(0);
    ps.removeSizeGradient(1);
    ps.addSizeGradient(0, born * 0.85, born * 1.3);
    ps.addSizeGradient(1, born * 1.9, born * 2.5);
  }

  // --- Construction helpers ---------------------------------------------------------------

  private base(name: string, capacity: number): ParticleSystem {
    const ps = new ParticleSystem(name, capacity, this.scene);
    ps.particleTexture = this.sprite;
    ps.emitter = new Vector3(0, 0, 0);
    ps.minEmitBox = new Vector3(0, 0, 0);
    ps.maxEmitBox = new Vector3(0, 0, 0);
    ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    ps.preWarmCycles = 0;
    ps.start();
    return ps;
  }

  /**
   * The depth charge's water: the 30 mm column's silhouette at ten times the size and a
   * fifth of the speed (Adam's direction).
   *
   * Same stretched-billboard trick as the splash, for the same reason — thrown water is
   * ligaments and streaks, never discs — but everything about its timing is different.
   * Gravity is roughly a third of real so the plume climbs for two and a half seconds
   * instead of a quarter of one, and the lifetime is long enough to let it fall back
   * rather than being cut off mid-air. Both are what "five times slower" actually means
   * for a ballistic particle: you cannot simply slow the launch, or it never leaves the
   * water.
   */
  private makeBurstWater(): ParticleSystem {
    const ps = this.base('burstWater', 1400);
    ps.particleTexture = this.spraySprite;
    ps.color1 = new Color4(0.94, 0.96, 0.98, 0.78);
    ps.color2 = new Color4(0.74, 0.8, 0.85, 0.62);
    ps.colorDead = new Color4(0.72, 0.78, 0.84, 0);
    ps.minLifeTime = 2.4;
    ps.maxLifeTime = 4.2;
    ps.emitRate = 0;
    ps.gravity = new Vector3(0, -3.4, 0);
    ps.isBillboardBased = true;
    ps.billboardMode = ParticleSystem.BILLBOARDMODE_STRETCHED;
    // Near-vertical: an explosion under the surface vents up, and a wide cone here
    // reads as a bloom rather than as a column.
    ps.direction1 = new Vector3(-0.22, 1, -0.22);
    ps.direction2 = new Vector3(0.22, 1, 0.22);
    return ps;
  }

  /**
   * The mist above the column — the part that says how much water was moved.
   *
   * Long-lived, large and thin. It rises with the plume and is still hanging after the
   * water has fallen back, which is the read Adam asked for: lots of mist, going up.
   */
  private makeBurstMist(): ParticleSystem {
    const ps = this.base('burstMist', 900);
    ps.particleTexture = this.smokeSprite;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    ps.minAngularSpeed = -0.25;
    ps.maxAngularSpeed = 0.25;
    ps.color1 = new Color4(0.92, 0.95, 0.97, 0.3);
    ps.color2 = new Color4(0.82, 0.87, 0.92, 0.2);
    ps.colorDead = new Color4(0.8, 0.85, 0.9, 0);
    ps.minLifeTime = 3.0;
    ps.maxLifeTime = 6.5;
    ps.emitRate = 0;
    // Barely falls: suspended droplets, not thrown water.
    ps.gravity = new Vector3(0, -0.55, 0);
    ps.direction1 = new Vector3(-0.45, 0.8, -0.45);
    ps.direction2 = new Vector3(0.45, 1, 0.45);
    return ps;
  }

  private makeSplash(): ParticleSystem {
    const ps = this.base('splash', 900);
    ps.particleTexture = this.spraySprite;
    ps.color1 = new Color4(0.92, 0.95, 0.97, SPLASH_ALPHA);
    ps.color2 = new Color4(0.72, 0.78, 0.82, SPLASH_ALPHA * 0.9);
    ps.colorDead = new Color4(0.7, 0.75, 0.8, 0);
    // The hard column: up fast, down fast, and wholly gone inside SPLASH_MAX_LIFE.
    ps.minLifeTime = 0.3;
    ps.maxLifeTime = Math.min(SPLASH_MAX_LIFE, IMPACT_MAX_LIFE);
    ps.emitRate = 0;
    ps.gravity = new Vector3(0, -9.81, 0);

    /*
     * Stretched along velocity, not facing the camera.
     *
     * This is what stops the splash reading as bubbles. A camera-facing quad shows the
     * same silhouette no matter how fast the particle is travelling, so every droplet is
     * a disc; stretching each one along its own velocity turns rising water into vertical
     * ligaments and falling water into streaks, which is how thrown water actually looks
     * and, incidentally, how it differs from the smoke that shares this class.
     */
    ps.isBillboardBased = true;
    ps.billboardMode = ParticleSystem.BILLBOARDMODE_STRETCHED;

    // A tight plume. A 30 mm round punches a narrow column straight up; the wide cone
    // that was here threw droplets sideways and read as a bloom rather than a hit.
    ps.direction1 = new Vector3(-0.16, 1, -0.16);
    ps.direction2 = new Vector3(0.16, 1, 0.16);
    // Stretched billboards take their orientation from velocity, so spin is meaningless.
    ps.minAngularSpeed = 0;
    ps.maxAngularSpeed = 0;
    return ps;
  }

  private makeSpray(): ParticleSystem {
    const ps = this.base('spray', 600);
    /*
     * The hanging mist gets the irregular mass too.
     *
     * This was left on the round sprite on the reasoning that mist is formless. That is
     * true of mist you are standing in and false of a mist cloud you are looking at from
     * two kilometres away, where it subtends a definite shape — and at 1.2 to 3.5 m times
     * a distance scale reaching 6.5x, these are eight-metre discs that outlive the column
     * that threw them. It is the longest-lived and largest part of an impact, so it was
     * the part still reading as bubbles after the column itself was fixed.
     */
    ps.particleTexture = this.smokeSprite;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    ps.minAngularSpeed = -0.5;
    ps.maxAngularSpeed = 0.5;
    ps.color1 = new Color4(0.9, 0.93, 0.96, SPRAY_ALPHA);
    ps.color2 = new Color4(0.8, 0.85, 0.9, SPRAY_ALPHA * 0.62);
    ps.colorDead = new Color4(0.8, 0.85, 0.9, 0);
    // The mist that hangs after the column falls. This is the part that used to run to
    // 3.2 s and read as smoke sitting on the water at long range.
    ps.minLifeTime = 0.8;
    ps.maxLifeTime = IMPACT_MAX_LIFE;
    ps.emitRate = 0;
    ps.gravity = new Vector3(0, -1.6, 0);
    ps.direction1 = new Vector3(-1, 0.5, -1);
    ps.direction2 = new Vector3(1, 1.2, 1);
    ps.minEmitPower = 3;
    ps.maxEmitPower = 9;
    return ps;
  }

  /**
   * A permanent near-horizontal emitter for pressure waves and compressed-air rings.
   *
   * Keeping this direction on the system is the important part: Babylon reads it later,
   * when `manualEmitCount` is consumed during particle animation.
   */
  private makeRingSpray(): ParticleSystem {
    const ps = this.base('ringSpray', 180);
    ps.particleTexture = this.smokeSprite;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    ps.minAngularSpeed = -0.5;
    ps.maxAngularSpeed = 0.5;
    ps.color1 = new Color4(0.9, 0.93, 0.96, SPRAY_ALPHA);
    ps.color2 = new Color4(0.8, 0.85, 0.9, SPRAY_ALPHA * 0.62);
    ps.colorDead = new Color4(0.8, 0.85, 0.9, 0);
    ps.minLifeTime = 0.8;
    ps.maxLifeTime = IMPACT_MAX_LIFE;
    ps.emitRate = 0;
    ps.gravity = new Vector3(0, -1.1, 0);
    ps.direction1 = new Vector3(-1, -0.05, -1);
    ps.direction2 = new Vector3(1, 0.22, 1);
    return ps;
  }

  /**
   * The torpedo wake.
   *
   * Its own system rather than a few borrowed splash puffs, because for most of a run
   * this is the entire weapon: the body is four metres down in opaque water and the only
   * thing the player — or the target — can see is the disturbed line on the surface. A
   * torpedo you cannot see is a torpedo that does not exist, which is exactly how the
   * first version read.
   *
   * Particles are dropped in place with almost no velocity of their own, so the trail
   * marks where the weapon actually went. Length is speed times lifetime: at running
   * speed that is roughly the sixty metres the plan asks for.
   */
  private makeWake(): ParticleSystem {
    /*
     * Capacity carries the whole trail at once: 560 m of streak at roughly a metre per
     * particle is several hundred alive simultaneously, and a pool that wrapped would
     * erase the far end of the wake mid-run.
     */
    const ps = this.base('torpedoWake', 2400);
    ps.particleTexture = this.spraySprite;
    // Alphas sit 20% under where they started, on Adam's call: the line should be
    // legible, not fluorescent.
    ps.color1 = new Color4(0.95, 0.97, 1.0, 0.58);
    ps.color2 = new Color4(0.78, 0.85, 0.92, 0.4);
    ps.colorDead = new Color4(0.8, 0.86, 0.92, 0);
    ps.minLifeTime = WAKE_LIFETIME * 0.75;
    ps.maxLifeTime = WAKE_LIFETIME;
    ps.emitRate = 0;
    // Barely any throw: the foam boils up where the screws passed and then spreads.
    ps.gravity = new Vector3(0, -0.25, 0);
    ps.direction1 = new Vector3(-0.5, 0.25, -0.5);
    ps.direction2 = new Vector3(0.5, 0.6, 0.5);
    ps.minEmitPower = 0.6;
    ps.maxEmitPower = 2.2;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    return ps;
  }

  private makeHitFlash(): ParticleSystem {
    const ps = this.base('torpedoHitFlash', 8);
    // Additive and HDR-hot: this is glare, not fire. Life is the whole design — the
    // particles exist for the flash's 50 ms and no longer.
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.color1 = new Color4(3.6, 2.8, 1.8, 1);
    ps.color2 = new Color4(2.6, 1.7, 0.9, 1);
    ps.colorDead = new Color4(1.0, 0.5, 0.2, 0);
    ps.minLifeTime = 0.04;
    ps.maxLifeTime = 0.06;
    ps.emitRate = 0;
    ps.minEmitPower = 0;
    ps.maxEmitPower = 0.4;
    return ps;
  }

  /** The 30 mm's own flash: additive, hot, and gone in four frames. */
  private makeMuzzleFlashPs(): ParticleSystem {
    const ps = this.base('muzzleFlashPuff', 24);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    // Hot but not white-hot, and low alpha: this reads as a flicker at the brake, not
    // as a fireball. HDR values stay modest so the bloom does not seize on it.
    ps.color1 = new Color4(2.4, 1.6, 0.7, 0.8);
    ps.color2 = new Color4(1.7, 0.95, 0.34, 0.6);
    ps.colorDead = new Color4(0.5, 0.25, 0.1, 0);
    ps.minLifeTime = 0.04;
    ps.maxLifeTime = 0.07;
    ps.emitRate = 0;
    ps.minSize = 0.9;
    ps.maxSize = 1.8;
    ps.minEmitPower = 0;
    ps.maxEmitPower = 0.6;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    return ps;
  }

  /** A hotter, longer-lived flash that remains legible on a firing vessel downrange. */
  private makeHostileMuzzleFlashPs(): ParticleSystem {
    const ps = this.base('hostileMuzzleFlash', 72);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.color1 = new Color4(3.8, 2.1, 0.7, 1);
    ps.color2 = new Color4(2.8, 1.0, 0.22, 0.86);
    ps.colorDead = new Color4(0.9, 0.22, 0.04, 0);
    ps.minLifeTime = 0.10;
    ps.maxLifeTime = 0.20;
    ps.emitRate = 0;
    ps.minEmitPower = 0.4;
    ps.maxEmitPower = 1.2;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    return ps;
  }

  private makeSparks(): ParticleSystem {
    const ps = this.base('sparks', 500);
    // Sparks keep the round sprite because stretching turns it into exactly what a spark
    // is: a streak along its own path. A glowing dot travelling 20 m/s is a line, not a
    // ball, and this is the one system where the circular gradient is the right input.
    ps.isBillboardBased = true;
    ps.billboardMode = ParticleSystem.BILLBOARDMODE_STRETCHED;
    ps.color1 = new Color4(3.4, 2.0, 0.7, 1);
    ps.color2 = new Color4(2.2, 0.9, 0.25, 1);
    ps.colorDead = new Color4(0.5, 0.2, 0.1, 0);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.minLifeTime = 0.25;
    ps.maxLifeTime = 0.8;
    ps.emitRate = 0;
    ps.gravity = new Vector3(0, -14, 0);
    ps.direction1 = new Vector3(-1, 0.2, -1);
    ps.direction2 = new Vector3(1, 1, 1);
    ps.minEmitPower = 8;
    ps.maxEmitPower = 22;
    return ps;
  }

  private makeBlood(): ParticleSystem {
    const ps = this.base('blood', 160);
    // The smoke sprite's lumpy silhouette makes globs; the round dot made luminous beads.
    ps.particleTexture = this.smokeSprite;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    // Dark arterial red crossing to a bruise purple, opaque and light-absorbing.
    ps.color1 = new Color4(0.46, 0.05, 0.10, 0.95);
    ps.color2 = new Color4(0.26, 0.05, 0.20, 0.9);
    ps.colorDead = new Color4(0.10, 0.02, 0.06, 0);
    ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    ps.minLifeTime = 0.3;
    ps.maxLifeTime = 0.8;
    ps.emitRate = 0;
    // Heavy: droplets barely leave the wound and fall, where sparks streak and scatter.
    ps.gravity = new Vector3(0, -26, 0);
    ps.direction1 = new Vector3(-1, 0.1, -1);
    ps.direction2 = new Vector3(1, 0.7, 1);
    ps.minEmitPower = 2;
    ps.maxEmitPower = 7;
    return ps;
  }

  private makeFireball(): ParticleSystem {
    const ps = this.base('fireball', 400);
    // Burning fuel rolls in the same lumpy masses its smoke does.
    ps.particleTexture = this.smokeSprite;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    ps.color1 = new Color4(3.0, 1.4, 0.4, 1);
    ps.color2 = new Color4(1.2, 0.4, 0.12, 1);
    ps.colorDead = new Color4(0.1, 0.09, 0.09, 0);
    ps.blendMode = ParticleSystem.BLENDMODE_ADD;
    ps.minLifeTime = 0.5;
    ps.maxLifeTime = 1.6;
    ps.emitRate = 0;
    ps.gravity = new Vector3(0, 2.5, 0);
    ps.direction1 = new Vector3(-1, 0.4, -1);
    ps.direction2 = new Vector3(1, 1.5, 1);
    ps.minEmitPower = 6;
    ps.maxEmitPower = 20;
    return ps;
  }

  private makeMuzzleSmoke(): ParticleSystem {
    const ps = this.base('muzzleSmoke', 240);
    ps.particleTexture = this.smokeSprite;
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    ps.minAngularSpeed = -1.1;
    ps.maxAngularSpeed = 1.1;
    /*
     * Big, and almost not there: five times the size at a twentieth of the opacity.
     *
     * Two passes with Adam got here. The gun sits three metres from the player now that
     * it has a visible mount, and the original plume — half-opaque, 2.4 m across, five
     * puffs a round at 300 rpm — was a grey wall across the sight picture. Cutting the
     * alpha ninety per cent fixed the wall but left a wisp; growing the puffs 5× and
     * halving the alpha again gives what a real gun makes at this range, a large soft
     * volume you see the sea straight through.
     */
    ps.color1 = new Color4(0.55, 0.53, 0.5, 0.0234);
    ps.color2 = new Color4(0.4, 0.4, 0.4, 0.0149);
    ps.colorDead = new Color4(0.35, 0.35, 0.35, 0);
    // Longer-lived to match the larger puffs: a 6 m cloud that vanishes in a third of a
    // second reads as a pop rather than as smoke drifting off a barrel.
    ps.minLifeTime = 0.45;
    ps.maxLifeTime = 1.0;
    ps.emitRate = 0;
    ps.minSize = 1.75;
    ps.maxSize = 6.0;
    ps.gravity = new Vector3(0, 1.2, 0);
    ps.minEmitPower = 4;
    ps.maxEmitPower = 11;
    return ps;
  }

  /**
   * The dying-ship column: thick, black, slow, and rising a long way. This is the
   * strongest read the player gets that something out there is finished.
   */
  /*
   * Retuned on Adam's direction (2026-07-28): billows 80% slower, 200% larger, 30%
   * more transparent. The knobs move together or the look falls apart — a fifth of the
   * rise speed with the old 6-15 s lifetimes topped the column out at a fifth of its
   * height, so life stretches 2.5× to buy the height back, capacity grows to hold the
   * longer-lived crowd, and the emit rate halves because tripled puffs more than carry
   * the density.
   */
  private makeSmokeColumn(): ParticleSystem {
    const ps = new ParticleSystem(`shipSmoke${this.smokeColumns.length}`, 3000, this.scene);
    ps.particleTexture = this.smokeSprite;
    // One texture across hundreds of particles reads as a repeated stamp unless each
    // copy is turned. With a lumpy silhouette the rotation is visible work, not waste.
    ps.minInitialRotation = 0;
    ps.maxInitialRotation = Math.PI * 2;
    ps.emitter = new Vector3(0, 0, 0);
    // A tight root. The old +/-3 m footprint let parcels start beside each other rather
    // than on top of each other, which is half of why the smoke fanned instead of
    // stacking — Adam wants "more of a single column".
    ps.minEmitBox = new Vector3(-1.2, 0, -1.2);
    ps.maxEmitBox = new Vector3(1.2, 0, 1.2);
    /*
     * Oily smoke, shaped over each parcel's life (Adam's direction): born small, dark
     * and dense at the fire, swelling to twice its emitted size and thinning to
     * nothing as it climbs. The colour ramp front-loads the fade — most of the alpha
     * is gone by two-thirds of the rise, so the top of the column is haze, not soot.
     * Gradients multiply/override the static colour properties, so those are gone.
     */
    // Size is bound per hull in setSmokeScale — see there for why it cannot live here.
    ps.addColorGradient(0, new Color4(0.05, 0.045, 0.04, 0.92), new Color4(0.11, 0.10, 0.09, 0.86));
    ps.addColorGradient(0.35, new Color4(0.17, 0.16, 0.16, 0.7), new Color4(0.25, 0.24, 0.24, 0.62));
    ps.addColorGradient(1, new Color4(0.4, 0.4, 0.42, 0), new Color4(0.45, 0.45, 0.47, 0));
    ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    /*
     * Slower again, and narrower (Adam, 2026-07-30: "rise much slower and in more of a
     * single column").
     *
     * Rise is roughly 40% of the last pass across all three things that carry it —
     * launch power, the direction cone's vertical component, and the buoyancy gravity —
     * and lifetimes stretch to match so the column still climbs as far as it did. That
     * pairing is the whole trick and has caught this effect out before: cutting speed
     * without extending life just tops the column out lower.
     *
     * The lateral spread comes right in. It was wider than the vertical component was
     * strong, so parcels drifted sideways about as fast as they climbed and the plume
     * read as a spreading smudge. Now it is a column that leans, which is what a ship
     * fire looks like from a mile away.
     */
    ps.minLifeTime = 34;
    ps.maxLifeTime = 82;
    ps.emitRate = 15;
    ps.gravity = new Vector3(0, 0.062, 0);
    ps.direction1 = new Vector3(-0.35, 1.15, -0.35);
    ps.direction2 = new Vector3(0.8, 2.7, 0.8);
    ps.minAngularSpeed = -0.1;
    ps.maxAngularSpeed = 0.1;
    ps.minEmitPower = 0.17;
    ps.maxEmitPower = 0.45;
    return ps;
  }

  dispose(): void {
    for (const ps of [
      this.splash, this.spray, this.ringSpray, this.sparks, this.blood,
      this.fireball, this.muzzleSmoke, this.muzzleFlashPs, this.hostileMuzzleFlashPs,
      this.hitFlashPs,
      this.burstWater, this.burstMist,
    ]) {
      ps.dispose();
    }
    for (const ps of this.wakes.values()) ps.dispose();
    for (const ps of this.wakePool) ps.dispose();
    this.hitFlashLight.dispose();
    for (const c of this.smokeColumns) c.system.dispose();
    for (const s of this.smokePool) s.dispose();
    for (const t of this.tracers) t.dispose();
    for (const streak of this.ricochetStreaks) streak.mesh.dispose();
    this.tracerProto.dispose();
    this.tracerMaterial.dispose();
    for (const charge of this.depthCharges) charge.dispose();
    this.depthChargeProto.dispose();
    this.depthChargeMaterial.dispose();
    this.muzzleLight.dispose();
    this.sprite.dispose();
    this.spraySprite.dispose();
    this.smokeSprite.dispose();
  }
}
